import React, { useEffect, useMemo, useState } from 'react';
import { Link }                              from 'react-router-dom';
import { listSessions, downloadReportPdf, shareReport } from '../api/sessions';
import EmptyState                            from '../components/ui/EmptyState';
import Spinner                               from '../components/ui/Spinner';
import IntegrityStatusBadge                  from '../components/IntegrityStatusBadge';

/**
 * ReportsPage - cross-session report directory (Phase 8).
 *
 * Lists ENDED sessions for the host with the server integrity status, the
 * signal count and per-row actions (View, Download PDF, Share).
 *
 * The table is built from {@link listSessions} `{ status: 'ENDED' }`; the backend
 * projects findingCount and integrityStatus onto SessionSummaryResponse.
 */
// I-P0-B: the retired 0-100 agent risk number and the "Advance / Reject" verdict that was
// derived from it are gone. Rows show the server integrity status (unreviewed signals only).
// Integrity never produces a hiring recommendation; capability lives on the report itself.
const STATUS_FILTERS = [
  { value: 'HIGH',          label: 'High review signals' },
  { value: 'MEDIUM',        label: 'Medium review signals' },
  { value: 'NONE',          label: 'No review signals' },
  { value: 'NOT_EVALUATED', label: 'Not evaluated' },
];

function statusOf(r) {
  const s = (r.integrityStatus || '').toUpperCase();
  return s === 'HIGH' || s === 'MEDIUM' || s === 'NONE' ? s : 'NOT_EVALUATED';
}

function SeverityPill({ label, count, color }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
          style={{ backgroundColor: `${color}15`, color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label} {count}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function ReportsPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [busy,    setBusy]    = useState(null); // sessionId currently downloading/sharing
  const [toast,   setToast]   = useState('');

  // Filters
  const [search,     setSearch]     = useState('');
  const [riskFilter, setRiskFilter] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('ALL');

  useEffect(() => {
    let cancelled = false;
    console.log('[ReportsPage] loading ended sessions');
    setLoading(true);
    listSessions({ status: 'ENDED', size: 200 })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.content ?? res.data ?? [];
        console.log('[ReportsPage] loaded', data.length, 'reports');
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ReportsPage] load failed:', err.message);
        setError(err.message || 'Failed to load reports.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const now = Date.now();
    const dateCutoff = dateFilter === 'WEEK'  ? now - 7  * 86400000
                     : dateFilter === 'MONTH' ? now - 30 * 86400000
                     : null;
    const q = search.trim().toLowerCase();

    return rows.filter((r) => {
      if (riskFilter !== 'ALL' && statusOf(r) !== riskFilter) return false;
      if (dateCutoff && r.endedAt && new Date(r.endedAt).getTime() < dateCutoff) return false;
      if (q) {
        const hay = `${r.code || ''} ${r.candidateEmail || ''} ${r.candidateName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, riskFilter, dateFilter]);

  const handleDownload = async (id, code) => {
    setBusy(id);
    try {
      const res  = await downloadReportPdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `report-${code || id}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[ReportsPage] PDF download failed:', err.message);
      setToast(`Download failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  const handleShare = async (id) => {
    setBusy(id);
    try {
      const res = await shareReport(id);
      const url = res.data?.url;
      if (url && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setToast('Share link copied to clipboard');
      } else {
        setToast(url || 'Share link generated');
      }
    } catch (err) {
      console.error('[ReportsPage] share failed:', err.message);
      setToast(`Share failed: ${err.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">Integrity</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Reports</h1>
          <p className="text-sm text-ink-faint mt-1 max-w-xl">
            Completed sessions — view findings, download PDFs, or share read-only links.
          </p>
        </div>
      </div>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 card p-4 rounded-xl">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by candidate or session code..."
          className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-line rounded-lg bg-surface text-ink
                     focus:outline-none focus:ring-2 focus:ring-teal-500/25 focus:border-teal-400"
        />
        <select
          value={riskFilter}
          onChange={(e) => setRiskFilter(e.target.value)}
          className="px-2.5 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-800"
        >
          <option value="ALL">All integrity statuses</option>
          {STATUS_FILTERS.map((b) => (
            <option key={b.value} value={b.value}>{b.label}</option>
          ))}
        </select>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-2.5 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-800"
        >
          <option value="ALL">All time</option>
          <option value="WEEK">Last 7 days</option>
          <option value="MONTH">Last 30 days</option>
        </select>
      </div>

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200/90 rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Spinner /></div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600">{error}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="📊"
            title="No reports yet"
            subtitle="Completed sessions will appear here with their integrity reports."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Session</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Ended</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Integrity</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Signals</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => {
                  const id    = r.sessionId ?? r.id;
                  return (
                    <tr key={id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.code || (typeof id === 'string' ? id.slice(0, 8) : id)}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-800">{r.candidateName || '-'}</div>
                        <div className="text-slate-500 text-xs">{r.candidateEmail || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{formatDate(r.endedAt)}</td>
                      <td className="px-4 py-3"><IntegrityStatusBadge status={r.integrityStatus} compact light /></td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <SeverityPill label="Crit" count={r.criticalCount} color="#dc2626" />
                          <SeverityPill label="High" count={r.highCount}     color="#f97316" />
                          <SeverityPill label="Total" count={r.findingCount ?? r.totalFindings} color="#64748b" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <Link to={`/report/${id}`}
                                className="px-2.5 py-1 text-xs font-medium rounded border border-slate-300 text-slate-700 hover:bg-slate-100">
                            View
                          </Link>
                          <button onClick={() => handleDownload(id, r.code)}
                                  disabled={busy === id}
                                  type="button"
                                  className="px-2.5 py-1 text-xs font-medium rounded-lg border border-teal-200 text-teal-700 hover:bg-teal-50 disabled:opacity-50">
                            PDF
                          </button>
                          <button onClick={() => handleShare(id)}
                                  disabled={busy === id}
                                  className="px-2.5 py-1 text-xs font-medium rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                            Share
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 px-4 py-2 bg-slate-900 text-white text-sm rounded-lg shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
